import { inject, Injectable, isDevMode } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil } from 'rxjs';
import { clearAllState } from '../../../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../../../user/store/selectors/user.selectors';
import { PatientHealthConcernsService } from '../../../services/patient-health-concerns/patient-health-concerns.service';
import {
  loadPatientHealthConcernById,
  loadPatientHealthConcernByIdFailure,
  loadPatientHealthConcernByIdSuccess,
  loadPatientHealthConcerns,
  loadPatientHealthConcernsFailure,
  loadPatientHealthConcernsSuccess,
  recordPatientHealthConcernDetailPrintEvent,
  recordPatientHealthConcernDetailPrintEventFailure,
  recordPatientHealthConcernDetailPrintEventSuccess,
  recordPatientHealthConcernPrintEvent,
  recordPatientHealthConcernPrintEventFailure,
  recordPatientHealthConcernPrintEventSuccess,
} from '../actions/patient-health-concern.actions';
import {
  selectCacheLoadedAt,
  selectCachePatientId,
  selectIsLoaded,
  selectPatientHealthConcernEntities,
  selectPatientHealthConcerns,
} from '../selectors/patient-health-concern.selector';

@Injectable()
export class PatientHealthConcernEffects {
  private readonly CACHE_TTL_MS = 5 * 60 * 1000;
  private actions$ = inject(Actions);
  private patientHealthConcernsService = inject(PatientHealthConcernsService);
  private store = inject(Store);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  loadPatientHealthConcerns$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadPatientHealthConcerns),
      concatLatestFrom(() => [
        this.store.select(selectIsLoaded),
        this.store.select(selectCachePatientId),
        this.store.select(selectCacheLoadedAt),
        this.store.select(selectPatientHealthConcerns),
      ]),
      mergeMap(([{ patientId }, isLoaded, cachePatientId, cacheLoadedAt, cachedHealthConcerns]) => {
        if (isLoaded && cachePatientId === patientId && this.isCacheFresh(cacheLoadedAt)) {
          if (isDevMode()) {
            console.debug('[HealthConcerns][Cache] Cache hit; skipping GetByPatientId call.', {
              patientId,
              count: cachedHealthConcerns.length,
            });
          }
          return of(
            loadPatientHealthConcernsSuccess({
              healthConcerns: cachedHealthConcerns,
              patientId,
              loadedAt: cacheLoadedAt ?? Date.now(),
            })
          );
        }

        return this.patientHealthConcernsService.getHealthConcernsByPatientId(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(healthConcerns =>
            loadPatientHealthConcernsSuccess({
              healthConcerns,
              patientId,
              loadedAt: Date.now(),
            })
          ),
          catchError(err => of(loadPatientHealthConcernsFailure({ error: err })))
        );
      })
    );
  });

  loadPatientHealthConcernById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadPatientHealthConcernById),
      concatLatestFrom(() => [
        this.store.select(selectPatientHealthConcernEntities),
        this.store.select(selectPatientId),
      ]),
      mergeMap(([{ patientHealthConcernId }, entities, patientId]) => {
        const cached = entities[patientHealthConcernId];
        if (cached) {
          if (isDevMode()) {
            console.debug('[HealthConcerns][Cache] Detail cache hit; skipping GetById call.', {
              patientHealthConcernId,
            });
          }
          return of(loadPatientHealthConcernByIdSuccess({ healthConcern: cached }));
        }

        return this.patientHealthConcernsService.getHealthConcernById(patientHealthConcernId, patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(healthConcern => loadPatientHealthConcernByIdSuccess({ healthConcern })),
          catchError(err => of(loadPatientHealthConcernByIdFailure({ error: err })))
        );
      })
    );
  });

  recordPrintEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(recordPatientHealthConcernPrintEvent),
      mergeMap(({ patientId, patientHealthConcernIds }) =>
        this.patientHealthConcernsService.postPrintEvent(patientId, patientHealthConcernIds).pipe(
          map(() => recordPatientHealthConcernPrintEventSuccess()),
          catchError(err => of(recordPatientHealthConcernPrintEventFailure({ error: err })))
        )
      )
    );
  });

  recordDetailPrintEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(recordPatientHealthConcernDetailPrintEvent),
      mergeMap(({ patientId, patientHealthConcernId }) =>
        this.patientHealthConcernsService.postDetailPrintEvent(patientId, patientHealthConcernId).pipe(
          map(() => recordPatientHealthConcernDetailPrintEventSuccess()),
          catchError(err => of(recordPatientHealthConcernDetailPrintEventFailure({ error: err })))
        )
      )
    );
  });

  private isCacheFresh(cacheLoadedAt: number | null): boolean {
    if (!cacheLoadedAt) {
      return false;
    }

    return Date.now() - cacheLoadedAt <= this.CACHE_TTL_MS;
  }
}
